import { Helmet } from "react-helmet";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../components/Header";
import { useState } from "react";
import {
  FaEye,
  FaPenToSquare,
  FaTrashCan,
  FaNetworkWired,
  FaCircleUser,
  FaHashtag,
  FaCircleNodes,
  FaServer,
  FaFileImport,
} from "react-icons/fa6";
import { BtnDefault } from "../../../../components/buttons/BtnDefault";
import { useNavigate } from "react-router-dom";
import { useHookOdc } from "../../../../hooks/useHookOdc";
import { CentralOdcConfirmDelete, ConfirmDeleteOdc } from "./Delete";
import { ImportOdc } from "./importData";
import { useHookCentralOdc } from "../../../../hooks/useHookCentralOdc";

/* ============================================
   Skeleton Loading
   ============================================ */
const SkeletonRow = ({ cols = 8 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-5 py-4">
        <div
          className="h-3.5 bg-slate-700/50 rounded-md"
          style={{ width: `${35 + Math.random() * 55}%` }}
        />
      </td>
    ))}
  </tr>
);

const SkeletonCard = () => (
  <div
    className="rounded-2xl p-4 animate-pulse"
    style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
  >
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl bg-slate-700/50 flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-slate-700/50 rounded-md w-32" />
        <div className="h-3 bg-slate-700/30 rounded-md w-24" />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-3 mt-3 pt-3" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}>
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-10 bg-slate-700/25 rounded-lg" />
      ))}
    </div>
  </div>
);

/* ============================================
   Status Badge
   ============================================ */
const StatusBadge = ({ status }) => {
  const isActive = status === "Aktif" || status === "aktif";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${isActive
        ? "bg-emerald-500/12 text-emerald-400 border border-emerald-500/20"
        : "bg-red-500/12 text-red-400 border border-red-500/20"
        }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-400" : "bg-red-400"}`} />
      {status}
    </span>
  );
};

/* ============================================
   Inline Action Button (Desktop)
   ============================================ */
const ActionBtn = ({ icon: Icon, label, onClick, color }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 cursor-pointer ${color === "#f87171" ? "hover:bg-red-500/15 active:scale-90" : "hover:opacity-80"}`}
    style={{ background: `${color}15`, border: `1px solid ${color}25` }}
    aria-label={label}
  >
    <Icon className="text-sm" style={{ color }} />
  </button>
);

/* ============================================
   Mobile Action Button
   ============================================ */
const MobileActionBtn = ({ icon: Icon, label, onClick, color }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 cursor-pointer`}
    style={{ background: `${color}15`, border: `1px solid ${color}25` }}
    onMouseEnter={(e) => { e.currentTarget.style.background = `${color}25`; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = `${color}15`; }}
    aria-label={label}
  >
    <Icon className="text-sm" style={{ color }} />
  </button>
);

/* ============================================
   Column Config
   ============================================ */
const columns = [
  { key: "no", label: "No", align: "center", width: "55px" },
  { key: "mainPort", label: "Port Utama", align: "center", width: "150px" },
  { key: "name", label: "Nama ODC", align: "left" },
  { key: "port", label: "Jumlah Port", align: "center", width: "150px" },
  { key: "odp", label: "Jumlah ODP", align: "center", width: "150px" },
  { key: "pppoe", label: "User PPPoE", align: "center", width: "180px" },
  { key: "action", label: "Aksi", align: "center", width: "130px" },
];

/* ============================================
   Numeric Badge (untuk angka di tabel)
   ============================================ */
const NumBadge = ({ value, icon: Icon, color, bg, border }) => (
  <span
    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold mx-auto"
    style={{ color, background: bg, border }}
  >
    <Icon className="text-[10px]" />
    {value ?? "-"}
  </span>
);

/* ============================================
   KOMPONEN: Odc
   ============================================ */
export const CentralOdc = () => {
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(true);
  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const { data, loading, refreshData, params, setParams } = useHookCentralOdc();


  return (
    <>
      <Helmet>
        <title>Management ODC Utama- Radius Trend</title>
        <meta
          name="description"
          content="Kelola data ODC — pantau port, splitter, jumlah ODP, dan user PPPoE yang terhubung ke setiap ODC."
        />
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <h2 className="text-base md:text-xl font-display font-bold text-gray-100">
            Management ODC Utama
          </h2>
          <div className="flex gap-2">
            <BtnDefault
              onClick={() => setOpenImport(true)}
              variant="secondary"
            >
              <span className="hidden lg:flex items-center gap-2">
                <FaFileImport className="text-xs" />
                Import
              </span>
              <FaFileImport className="lg:hidden text-xs" />
            </BtnDefault>
            <BtnDefault onClick={() => navigate("/dashboard/master-data/central-odc/create")}>
              <span className="hidden sm:inline">Tambah ODC</span>
              <span className="sm:hidden">Tambah</span>
            </BtnDefault>
          </div>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-5">
          {/* ===== Search ===== */}
          <section
            className="rounded-2xl p-4"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Pencarian ODC"
          >
            <div className="w-full max-w-sm">
              <div className="relative">
                <i
                  className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  placeholder="Cari nama ODC..."
                  value={params.search}
                  onChange={(e) => setParams({ ...params, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                />
              </div>
            </div>
          </section>

          {/* ===== Desktop Table ===== */}
          <section
            className="hidden lg:block rounded-2xl overflow-hidden"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Tabel ODC"
          >
            <div className="overflow-x-auto">
              <table className="min-w-max max-w-screen-2xl text-sm">
                {/* Head */}
                <thead>
                  <tr
                    className="border-b"
                    style={{ background: "rgba(15, 23, 42, 0.7)", borderColor: "rgba(51, 65, 85, 0.3)" }}
                  >
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${col.align === "center" ? "text-center" : "text-left"
                          }`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Body */}
                <tbody className="divide-y" style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}>
                  {loading && !data && (
                    <>{[...Array(6)].map((_, i) => <SkeletonRow key={i} cols={columns.length} />)}</>
                  )}

                  {!loading && (!data || data?.length === 0) && (
                    <tr>
                      <td colSpan={columns.length} className="px-5 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center"
                            style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                          >
                            <FaNetworkWired className="text-2xl text-slate-600" />
                          </div>
                          <div>
                            <p className="text-slate-400 font-medium">Tidak ada data ODC ditemukan</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {params.search ? "Coba ubah kata kunci pencarian" : "Belum ada ODC yang terdaftar"}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    data?.map((item, index) => (
                      <tr
                        key={item.id}
                        className="transition-colors duration-150"
                        style={{ background: "transparent" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0, 180, 216, 0.04)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      >
                        {/* No */}
                        <td className="px-5 py-3.5 text-center text-slate-500 font-mono text-xs">
                          {data.indexOf(item) + 1}
                        </td>

                        <td className="px-5 py-3.5 text-center">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold text-ocean-400 bg-ocean-500/8 border border-ocean-500/10">
                            <FaServer className="text-[10px]" />
                            {item.main_port ?? "-"}
                          </span>
                        </td>


                        {/* Nama + Icon */}
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{
                                background: "linear-gradient(135deg, rgba(0,180,216,0.15), rgba(0,119,182,0.08))",
                                border: "1px solid rgba(0,180,216,0.15)",
                              }}
                            >
                              <FaNetworkWired className="text-xs text-ocean-500" />
                            </div>
                            <span className="font-semibold text-gray-100">{item.name}</span>
                          </div>
                        </td>

                        {/* Port */}
                        <td className="px-5 py-3.5 text-center">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold text-ocean-400 bg-ocean-500/8 border border-ocean-500/10">
                            <FaServer className="text-[10px]" />
                            {item.used_port ?? "-"} / {item.port ?? "-"}
                          </span>
                        </td>

                        {/* ODP */}
                        <td className="px-5 py-3.5 text-center">
                          <NumBadge
                            value={item.odp}
                            icon={FaHashtag}
                            color="#34d399"
                            bg="rgba(52, 211, 153, 0.08)"
                            border="rgba(52, 211, 153, 0.12)"
                          />
                        </td>

                        {/* User PPPoE */}
                        <td className="px-5 py-3.5 text-center">
                          <NumBadge
                            value={item.customer}
                            icon={FaCircleUser}
                            color="#48CAE4"
                            bg="rgba(72, 202, 228, 0.08)"
                            border="rgba(72, 202, 228, 0.12)"
                          />
                        </td>

                        {/* Aksi */}
                        <td className="px-5 py-3.5">
                          <div className="flex items-center justify-center gap-2">
                            <ActionBtn icon={FaEye} label="Detail" color="#48CAE4" onClick={() => navigate(`/dashboard/master-data/central-odc/detail/${item.id}`)} />
                            <ActionBtn icon={FaPenToSquare} label="Edit" color="#a78bfa" />
                            <ActionBtn icon={FaTrashCan} label="Hapus" color="#f87171"
                              onClick={() => {
                                setDeleteId(item.id);
                                setOpenModalDelete(true);
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ===== Mobile Cards ===== */}
          <section className="lg:hidden space-y-3" aria-label="Daftar ODC">
            {loading && !data && <>{[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}</>}

            {!loading && (!data || data?.length === 0) && (
              <div
                className="rounded-2xl p-12 text-center"
                style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                >
                  <FaNetworkWired className="text-2xl text-slate-600" />
                </div>
                <p className="text-slate-400 font-medium">Tidak ada ODC ditemukan</p>
                <p className="text-xs text-slate-500 mt-1">{params.search ? "Coba ubah pencarian" : "Belum ada data ODC"}</p>
              </div>
            )}

            {!loading &&
              data?.map((item) => (
                <article
                  key={item.id}
                  className="rounded-2xl overflow-hidden transition-all duration-200"
                  style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "rgba(0, 180, 216, 0.15)";
                    e.currentTarget.style.background = "rgba(15, 23, 42, 0.7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(51, 65, 85, 0.3)";
                    e.currentTarget.style.background = "rgba(15, 23, 42, 0.5)";
                  }}
                >
                  {/* Header */}
                  <div className="p-4 flex items-start gap-3" style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.15)" }}>
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        background: "linear-gradient(135deg, rgba(0,180,216,0.15), rgba(0,119,182,0.08))",
                        border: "1px solid rgba(0,180,216,0.15)",
                      }}
                    >
                      <FaNetworkWired className="text-base text-ocean-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-semibold text-gray-100 text-sm truncate">{item.name}</h3>
                        <StatusBadge status={item.status} />
                      </div>
                      <p className="font-mono text-xs text-ocean-400/80 bg-ocean-500/8 px-2 py-0.5 rounded-lg border border-ocean-500/10 inline-block w-fit mt-1">
                        Port: {item.port ?? "-"}
                      </p>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="p-4 grid grid-cols-3 gap-3" style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.15)" }}>
                    {[
                      { icon: FaServer, label: "Port", value: item.port, color: "#48CAE4", bg: "rgba(72,202,228,0.08)", border: "rgba(72,202,228,0.12)" },
                      { icon: FaHashtag, label: "Spliter", value: item.splitter, color: "#fbbf24", bg: "rgba(251,191,36,0.06)", border: "rgba(251,191,36,0.1)" },
                      { icon: FaCircleNodes, label: "ODC", value: item.odc, color: "#a78bfa", bg: "rgba(167,139,250,0.08)", border: "rgba(167,139,250,0.12)" },
                    ].map((stat, i) => {
                      const Icon = stat.icon;
                      return (
                        <div
                          key={i}
                          className="text-center p-2.5 rounded-xl"
                          style={{ background: stat.bg, border: `1px solid ${stat.border}` }}
                        >
                          <Icon className="text-sm mx-auto mb-1" style={{ color: stat.color }} />
                          <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider leading-none mb-1">{stat.label}</p>
                          <p className="text-lg font-display font-bold leading-none" style={{ color: stat.color }}>
                            {stat.value ?? "-"}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  {/* ODP & PPPoE Row */}
                  <div className="px-4 pb-4 grid grid-cols-2 gap-3">
                    <div
                      className="flex items-center gap-2.5 p-3 rounded-xl"
                      style={{ background: "rgba(52, 211, 153, 0.05)", border: "1px solid rgba(52, 211, 153, 0.1)" }}
                    >
                      <FaHashtag className="text-xs text-emerald-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] text-slate-600 uppercase font-semibold tracking-wider leading-none">ODP</p>
                        <p className="text-sm font-bold text-gray-100 mt-0.5">{item.odp ?? 0}</p>
                      </div>
                    </div>
                    <div
                      className="flex items-center gap-2.5 p-3 rounded-xl"
                      style={{ background: "rgba(72, 202, 228, 0.05)", border: "1px solid rgba(72, 202, 228, 0.1)" }}
                    >
                      <FaCircleUser className="text-xs text-ocean-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] text-slate-600 uppercase font-semibold tracking-wider leading-none">User PPoE</p>
                        <p className="text-sm font-bold text-gray-100 mt-0.5">{item.pppoe ?? 0}</p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="px-4 py-3 flex justify-end gap-2">
                    <MobileActionBtn icon={FaEye} label="Detail" color="#48CAE4" onClick={() => navigate(`/dashboard/master-data/central-odc/detail/${item.id}`)} />
                    <MobileActionBtn icon={FaPenToSquare} label="Edit" color="#a78bfa" />
                    <MobileActionBtn icon={FaTrashCan} label="Hapus" color="#f87171"
                      onClick={() => {
                        setDeleteId(item.id);
                        setOpenModalDelete(true);
                      }}
                    />
                  </div>
                </article>
              ))}
          </section>
        </main>
      </DashboardLayout>

      {/* ===== Modal Import ===== */}
      <ImportOdc
        openModal={openImport}
        setOpenModal={setOpenImport}
        refresh={refreshData}
      />

      {/* ===== Modal Delete ===== */}
      <CentralOdcConfirmDelete
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        id={deleteId}
        refresh={refreshData}

      />

    </>
  );
};